/**
 * Slice E (web) — Batch detail (contribution log + lock).
 *
 * Web port of the mobile `apps/mobile/app/batches/[id].tsx` screen. Renders:
 *   - a batch header (short id, cooperative, status pill, total kg),
 *   - the contribution log (farmer + kg + date, newest first per backend),
 *   - an add-contribution form (shown only when status === 'open'), and
 *   - a Lock action (PATCH /batches/:id/lock), available only while open.
 *
 * Lock + add-contribution mirror the mobile guard: the backend rejects with
 * 400 when the batch isn't `open` (lock) / when contributions are added to a
 * non-open batch. We hide those affordances client-side AND surface
 * `FseApiError.message` if the server still rejects (e.g. a stale view where
 * the batch was locked in another tab).
 *
 * Unlike mobile, web doesn't enqueue to a sync outbox — it's online-only, so
 * a transport failure just surfaces as an error (no offline queue here).
 *
 * Uses the generated orval client for /batches + /contributions (TD-070);
 * farmer picker reuses the generated `farmersControllerList`.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Icon,
  InputLabel,
  ListItem,
  MenuItem,
  Money,
  Select,
  Snackbar,
  Stack,
  StatCard,
  StatusPill,
  Chip,
  Text,
  TextField,
  type StatusPillVariant,
} from '@fse/ui';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError, readAccessTokenClaims } from '../api/client';
import { QRCodeSVG } from 'qrcode.react';
import { microToZarp } from '../api/market';
import { publishListing } from '../api/marketplace';
import { getMyCollectionPoint, setMyCollectionPoint } from '../api/cooperatives';
import { groupDigits } from '../lib/format';
import { useTraceControllerRecordEvent } from '../api/generated/trace/trace';
import {
  getMarketControllerGetMatchesQueryKey,
  useMarketControllerGetMatches,
} from '../api/generated/market/market';
import type { MatchDto } from '../api/generated/models';
import { farmersControllerList } from '../api/generated/farmers/farmers';
import {
  getBatchesControllerGetOneQueryKey,
  getBatchesControllerListQueryKey,
  useBatchesControllerGetOne,
  useBatchesControllerLock,
} from '../api/generated/batches/batches';
import {
  getContributionsControllerListQueryKey,
  useContributionsControllerCreate,
  useContributionsControllerList,
} from '../api/generated/contributions/contributions';
import {
  ContributionDtoQualityGrade,
  type BatchDto,
  type BatchDtoStatus as BatchStatus,
  type ContributionListDto,
} from '../api/generated/models';

// Generated `ContributionDtoQualityGrade` is the nullable union ('A'|'B'|'C'|null);
// the picker + record keys need the non-null variant. Derive it from the const.
type QualityGrade = (typeof ContributionDtoQualityGrade)[keyof typeof ContributionDtoQualityGrade];

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});
const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const STATUS_LABEL: Record<BatchStatus, string> = {
  open: 'Open',
  locked: 'Locked',
  sold: 'Sold',
  settled: 'Settled',
  cancelled: 'Cancelled',
};

// StatusPill variants per batch status — mirrors _app.batches.tsx.
const STATUS_PILL: Record<BatchStatus, StatusPillVariant> = {
  open: 'active',
  locked: 'due',
  sold: 'sold',
  settled: 'active',
  cancelled: 'overdue',
};

const GRADES: Array<QualityGrade | ''> = ['', 'A', 'B', 'C'];

// Trace-event kinds offered in the "Record trace event" picker. These flow
// straight to POST /trace/events as `eventKind`; the canonical-JSON content
// hash is computed server-side. Kept small + supply-chain oriented for a batch.
const TRACE_EVENT_KINDS = ['harvest', 'load', 'deliver', 'lock'] as const;
type TraceEventKind = (typeof TRACE_EVENT_KINDS)[number];

/**
 * Coarse distance band the matcher exposes (the only geo value the server returns —
 * precise coordinates never leave the API). Same bands as the marketplace listing
 * distance chip. `distanceBand` isn't on the generated MatchDto yet (orval not
 * regenerated for this field), so we widen the match type at the read site.
 */
type DistanceBand = 'le25km' | 'le50km' | 'le100km' | 'gt100km';
type MatchWithDistance = MatchDto & { distanceBand?: DistanceBand | null };

function distanceBandLabel(t: TFunction, band: DistanceBand): string {
  switch (band) {
    case 'le25km':
      return t('marketplace.distance.le25km', 'Within 25 km');
    case 'le50km':
      return t('marketplace.distance.le50km', '25–50 km');
    case 'le100km':
      return t('marketplace.distance.le100km', '50–100 km');
    case 'gt100km':
      return t('marketplace.distance.gt100km', 'Over 100 km');
  }
}

function shortId(id: string): string {
  return id.slice(0, 8);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** The public provenance URL for a batch — what the QR encodes + the share link. */
function verifyUrl(batchId: string): string {
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';
  return `${origin}/verify/batch/${batchId}`;
}

export function BatchDetailScreen() {
  // Route is wired manually in router.ts with path '/batches/$id'. Read the
  // param loosely (strict: false) so this file doesn't depend on a generated
  // route-id type that doesn't exist in the manually-assembled tree.
  const params = useParams({ strict: false }) as { id?: string };
  const batchId = params.id ?? '';

  const batchQuery = useBatchesControllerGetOne(batchId, {
    query: { enabled: batchId.length > 0 },
  });

  const contributionsParams = { batchId, limit: 200, offset: 0 };
  const contributionsQuery = useContributionsControllerList(contributionsParams, {
    query: { enabled: batchId.length > 0 },
  });

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Text variant="headline-medium" as="h2">
            {batchQuery.data ? `Batch ${shortId(batchQuery.data.id)}` : 'Batch'}
          </Text>
          {batchQuery.data ? (
            <Box sx={{ mt: 0.5 }}>
              <Text variant="body-medium" color="text.secondary">
                {batchQuery.data.cooperativeName}
              </Text>
            </Box>
          ) : null}
        </Box>
        <Link to="/batches" style={{ textDecoration: 'none' }}>
          <Button variant="text">Back to batches</Button>
        </Link>
      </Box>

      {batchQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : batchQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => batchQuery.refetch()}>
              Retry
            </Button>
          }
        >
          Couldn't load this batch — tap retry. {String(batchQuery.error)}
        </Alert>
      ) : (
        <BatchDetailBody
          batch={batchQuery.data}
          contributions={contributionsQuery}
          contributionsParams={contributionsParams}
        />
      )}
    </Stack>
  );
}

interface BatchDetailBodyProps {
  batch: BatchDto;
  contributions: UseQueryResult<ContributionListDto, unknown>;
  contributionsParams: { batchId: string; limit: number; offset: number };
}

function BatchDetailBody({ batch, contributions, contributionsParams }: BatchDetailBodyProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isOpen = batch.status === 'open';

  const lockMutation = useBatchesControllerLock({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getBatchesControllerGetOneQueryKey(batch.id) });
        queryClient.invalidateQueries({ queryKey: getBatchesControllerListQueryKey() });
      },
    },
  });

  function invalidateAfterContribution() {
    queryClient.invalidateQueries({
      queryKey: getContributionsControllerListQueryKey(contributionsParams),
    });
    // total_kg + contributionCount on the batch change too.
    queryClient.invalidateQueries({ queryKey: getBatchesControllerGetOneQueryKey(batch.id) });
    queryClient.invalidateQueries({ queryKey: getBatchesControllerListQueryKey() });
  }

  const rows = contributions.data?.data ?? [];

  return (
    <Stack spacing={3}>
      {/* Header card */}
      <Card>
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                <Text variant="title-large" as="span">
                  {groupDigits(batch.totalKg)} kg
                </Text>
                <StatusPill variant={STATUS_PILL[batch.status]}>
                  {STATUS_LABEL[batch.status]}
                </StatusPill>
              </Stack>
              <Text variant="body-medium" color="text.secondary">
                {batch.contributionCount} contribution{batch.contributionCount === 1 ? '' : 's'} ·
                opened {dateFormatter.format(new Date(batch.openedAt))}
                {batch.lockedAt
                  ? ` · locked ${dateFormatter.format(new Date(batch.lockedAt))}`
                  : ''}
              </Text>
            </Stack>

            {isOpen ? (
              <Button
                variant="outlined"
                onClick={() => lockMutation.mutate({ id: batch.id })}
                disabled={lockMutation.isPending}
              >
                {lockMutation.isPending ? 'Locking…' : 'Lock batch'}
              </Button>
            ) : null}
          </Stack>

          {lockMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {lockMutation.error instanceof FseApiError
                ? lockMutation.error.message
                : `Could not lock the batch. ${String(lockMutation.error)}`}
            </Alert>
          ) : null}

          {!isOpen ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              This batch is {STATUS_LABEL[batch.status].toLowerCase()}. Contributions can only be
              added while a batch is open.
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {/* Provenance / QR + record trace event */}
      <ProvenanceSection batchId={batch.id} t={t} />

      {/* Matches against open buyer demands */}
      <MatchesSection batchId={batch.id} t={t} />

      {/* List on marketplace — once the batch is locked */}
      <PublishListingSection batchId={batch.id} status={batch.status} />

      {/* Add contribution — only when open */}
      {isOpen ? (
        <AddContributionForm batchId={batch.id} onAdded={invalidateAfterContribution} />
      ) : null}

      {/* Contribution log */}
      <Stack spacing={1.5}>
        <Text variant="title-large" as="h3">
          Contribution log
        </Text>

        {contributions.isPending ? (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            </CardContent>
          </Card>
        ) : contributions.isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => contributions.refetch()}>
                Retry
              </Button>
            }
          >
            Couldn't load contributions — tap retry. {String(contributions.error)}
          </Alert>
        ) : rows.length === 0 ? (
          <Card>
            <CardContent>
              <Text variant="body-medium" color="text.secondary">
                No contributions yet.
                {isOpen
                  ? ' Use the form above to log the first farmer delivery.'
                  : ' This batch was locked before any contributions were recorded.'}
              </Text>
            </CardContent>
          </Card>
        ) : (
          <Box
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
          >
            {rows.map((c, idx) => (
              <ListItem
                key={c.id}
                leadingIcon="person"
                title={c.farmerDisplayName || c.farmerUserId}
                supportingText={`${c.qtyKg} kg · harvested ${c.harvestDate} · recorded ${dateTimeFormatter.format(new Date(c.recordedAt))}`}
                trailing={
                  c.qualityGrade ? (
                    <Chip kind="assist" color="tertiary" label={c.qualityGrade} />
                  ) : (
                    <Text variant="body-medium" color="text.secondary">
                      —
                    </Text>
                  )
                }
                noDivider={idx === rows.length - 1}
              />
            ))}
          </Box>
        )}
      </Stack>
    </Stack>
  );
}

interface AddContributionFormProps {
  batchId: string;
  onAdded: () => void;
}

function AddContributionForm({ batchId, onAdded }: AddContributionFormProps) {
  const [farmerUserId, setFarmerUserId] = useState('');
  const [qtyKg, setQtyKg] = useState('');
  const [grade, setGrade] = useState<QualityGrade | ''>('');
  const [harvestDate, setHarvestDate] = useState(todayIso());

  // Farmer picker — reuse the generated farmers list. Scoped server-side to
  // the caller's cooperative, same as the batch.
  const farmersQuery = useQuery({
    queryKey: ['farmers', { limit: 100, offset: 0 }],
    queryFn: () => farmersControllerList({ limit: 100, offset: 0 }),
  });

  const createMutation = useContributionsControllerCreate({
    mutation: {
      onSuccess: () => {
        setFarmerUserId('');
        setQtyKg('');
        setGrade('');
        setHarvestDate(todayIso());
        onAdded();
      },
    },
  });

  const qtyNum = Number(qtyKg);
  const qtyValid = qtyKg.trim().length > 0 && Number.isFinite(qtyNum) && qtyNum > 0;
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(harvestDate);
  const canSubmit =
    farmerUserId.length > 0 && qtyValid && dateValid && !createMutation.isPending;

  const farmers = useMemo(() => farmersQuery.data?.data ?? [], [farmersQuery.data]);

  function submit() {
    createMutation.mutate({
      data: {
        batchId,
        farmerUserId,
        qtyKg: qtyNum,
        // Backend treats grade as optional/nullable — omit when unset.
        qualityGrade: grade === '' ? null : grade,
        harvestDate,
      },
    });
  }

  return (
    <Card variant="outlined">
      <CardHeader title="Add contribution" subheader="Record a farmer's delivery into this batch." />
      <CardContent>
        <Stack spacing={2}>
          <FormControl fullWidth disabled={farmersQuery.isPending || createMutation.isPending}>
            <InputLabel id="farmer-label">Farmer</InputLabel>
            <Select
              labelId="farmer-label"
              label="Farmer"
              value={farmerUserId}
              onChange={(e) => setFarmerUserId(String(e.target.value))}
            >
              {farmers.length === 0 ? (
                <MenuItem value="" disabled>
                  {farmersQuery.isPending ? 'Loading farmers…' : 'No farmers found'}
                </MenuItem>
              ) : (
                farmers.map((f) => (
                  <MenuItem key={f.userId} value={f.userId}>
                    {f.displayName}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {farmersQuery.isError ? (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={() => farmersQuery.refetch()}>
                  Retry
                </Button>
              }
            >
              Couldn't load the farmer list — tap retry. {String(farmersQuery.error)}
            </Alert>
          ) : null}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Quantity (kg)"
              type="number"
              value={qtyKg}
              onChange={(e) => setQtyKg(e.target.value)}
              error={qtyKg.length > 0 && !qtyValid}
              helperText={qtyKg.length > 0 && !qtyValid ? 'Enter a positive number' : ' '}
              disabled={createMutation.isPending}
              fullWidth
            />
            <FormControl sx={{ minWidth: 140 }} disabled={createMutation.isPending}>
              <InputLabel id="grade-label">Grade</InputLabel>
              <Select
                labelId="grade-label"
                label="Grade"
                value={grade}
                onChange={(e) => setGrade(e.target.value as QualityGrade | '')}
              >
                {GRADES.map((g) => (
                  <MenuItem key={g === '' ? 'none' : g} value={g}>
                    {g === '' ? 'Unspecified' : g}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Harvest date"
              type="date"
              value={harvestDate}
              onChange={(e) => setHarvestDate(e.target.value)}
              error={harvestDate.length > 0 && !dateValid}
              helperText={harvestDate.length > 0 && !dateValid ? 'YYYY-MM-DD' : ' '}
              slotProps={{ inputLabel: { shrink: true } }}
              disabled={createMutation.isPending}
              fullWidth
            />
          </Stack>

          {createMutation.isError ? (
            <Alert severity="error">
              {createMutation.error instanceof FseApiError
                ? createMutation.error.message
                : `Could not add the contribution. ${String(createMutation.error)}`}
            </Alert>
          ) : null}

          <Divider />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={submit}
              disabled={!canSubmit}
              startIcon={createMutation.isPending ? <CircularProgress size={16} /> : undefined}
            >
              {createMutation.isPending ? 'Adding…' : 'Add contribution'}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

interface ProvenanceSectionProps {
  batchId: string;
  t: TFunction;
}

/**
 * Public-provenance share affordance + "Record trace event" dialog.
 *
 * Shows a copyable public-verifier link (`/verify/batch/<id>`) that a buyer can
 * open from a phone, plus the eventKind picker + note that POSTs to
 * /trace/events. The recorded event becomes visible on the public viewer.
 *
 * QR: a `qrcode.react` dependency is needed to render the URL as a scannable QR
 * code. That dep is NOT added here (it's flagged for the orchestrator). Until
 * it lands we render the copyable link as the stand-in — see the TODO below.
 */
function ProvenanceSection({ batchId, t }: ProvenanceSectionProps) {
  const url = verifyUrl(batchId);

  const [copied, setCopied] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  const [eventKind, setEventKind] = useState<TraceEventKind>('harvest');
  const [note, setNote] = useState('');

  const recordMutation = useTraceControllerRecordEvent({
    mutation: {
      onSuccess: () => {
        setEventOpen(false);
        setNote('');
      },
    },
  });

  function recordEvent() {
    recordMutation.mutate({
      data: {
        subjectKind: 'batch',
        subjectId: batchId,
        eventKind,
        occurredAt: new Date().toISOString(),
        // The note is the only free-form field; redaction is applied server-side
        // before the payload is shown on the public viewer.
        payload: note.trim() ? { note: note.trim() } : {},
      },
    });
  }

  function copyLink() {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard
        .writeText(url)
        .then(() => setCopied(true))
        .catch(() => setCopied(false));
    }
  }

  function closeEvent() {
    if (recordMutation.isPending) return;
    recordMutation.reset();
    setEventOpen(false);
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title={t('batches.shareQr.title')}
        subheader={t('batches.shareQr.subtitle')}
      />
      <CardContent>
        <Stack spacing={2}>
          {/* The verify URL is the QR payload — a phone camera resolves it
              straight to the public provenance viewer (the Phase 3 exit
              criterion). White padding keeps the QR scannable in dark mode. */}
          <Box
            sx={{
              p: 2,
              alignSelf: 'center',
              // QR quiet zone must stay white in BOTH light and dark modes
              // for scannability — intentionally NOT theme-mode-dependent.
              bgcolor: 'common.white',
              borderRadius: 2,
              lineHeight: 0,
            }}
          >
            <QRCodeSVG value={url} size={176} level="M" />
          </Box>

          <TextField
            label={t('batches.shareQr.linkLabel')}
            value={url}
            slotProps={{ input: { readOnly: true } }}
            fullWidth
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Icon name="share" />}
              onClick={copyLink}
            >
              {copied ? t('batches.shareQr.copied') : t('batches.shareQr.copy')}
            </Button>
            <Button
              variant="contained"
              startIcon={<Icon name="event" />}
              onClick={() => {
                recordMutation.reset();
                setEventOpen(true);
              }}
            >
              {t('trace.record.action')}
            </Button>
            {/* In-app entry to the provenance timeline (TD-104 web side): the
                full viewer already exists at the public /verify route — link to
                it in-SPA so a coop/group admin can review the recorded trail
                without leaving the app. */}
            <Link
              to="/verify/$kind/$id"
              params={{ kind: 'batch', id: batchId }}
              style={{ textDecoration: 'none' }}
            >
              <Button variant="text" startIcon={<Icon name="timeline" />}>
                {t('trace.viewTimeline')}
              </Button>
            </Link>
          </Stack>
        </Stack>
      </CardContent>

      {/* Record trace event dialog */}
      <Dialog open={eventOpen} onClose={closeEvent} fullWidth maxWidth="sm">
        <DialogTitle>{t('trace.record.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('trace.record.body')}
          </DialogContentText>
          <Stack spacing={2}>
            <FormControl fullWidth disabled={recordMutation.isPending}>
              <InputLabel id="trace-kind-label">
                {t('trace.record.kindLabel')}
              </InputLabel>
              <Select
                labelId="trace-kind-label"
                label={t('trace.record.kindLabel')}
                value={eventKind}
                onChange={(e) => setEventKind(e.target.value as TraceEventKind)}
              >
                {TRACE_EVENT_KINDS.map((kind) => (
                  <MenuItem key={kind} value={kind}>
                    {t(`trace.eventKind.${kind}`, kind)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('trace.record.noteLabel')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              minRows={2}
              maxRows={5}
              slotProps={{ htmlInput: { maxLength: 500 } }}
              disabled={recordMutation.isPending}
              fullWidth
            />
          </Stack>
          {recordMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t('trace.record.error', {
                error:
                  recordMutation.error instanceof Error
                    ? recordMutation.error.message
                    : String(recordMutation.error),
              })}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={closeEvent} disabled={recordMutation.isPending}>
            {t('trace.record.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={recordEvent}
            disabled={recordMutation.isPending}
            startIcon={recordMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t('trace.record.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message={t('batches.shareQr.copied')}
      />
    </Card>
  );
}

// --------------------------------------------------------------------------
// Publish to marketplace (Phase 5). A locked/sold batch can be listed so verified
// buyers make offers; the seller sets the asking price per kg. publishListing
// validates ownership + listable state server-side, so we surface its error.
// --------------------------------------------------------------------------

/** "12.50" R/kg -> micro-ZARP integer STRING, or null if not a positive number. */
function zarPerKgToMicroString(input: string): string | null {
  const n = Number(input);
  if (!Number.isFinite(n) || n <= 0) return null;
  return String(Math.round(n * 1_000_000));
}

function PublishListingSection({ batchId, status }: { batchId: string; status: BatchStatus }) {
  const [price, setPrice] = useState('');
  const [note, setNote] = useState('');
  const [listedId, setListedId] = useState<string | null>(null);
  const micro = zarPerKgToMicroString(price);
  const valid = micro != null;

  const publish = useMutation({
    mutationFn: () =>
      publishListing({
        sourceKind: 'batch',
        sourceId: batchId,
        askPriceZarpPerKgMicro: micro as string,
        note: note.trim() ? note.trim() : undefined,
      }),
    onSuccess: (listing) => setListedId(listing.id),
  });

  // A batch is only sellable once locked (open batches still accept
  // contributions). The server is the authority; this just hides the affordance.
  if (status === 'open') return null;

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Text variant="title-medium" as="span">
            List on marketplace
          </Text>
          {listedId ? (
            <Alert severity="success">
              Listed on the marketplace.{' '}
              <Link to="/my-listings" style={{ color: 'inherit', fontWeight: 600 }}>
                View your listings
              </Link>
            </Alert>
          ) : (
            <>
              <Text variant="body-small" color="text.secondary">
                Publish this batch so verified buyers can make offers. You set the asking price per
                kilogram and accept or reject each offer.
              </Text>
              <TextField
                label="Asking price (R / kg)"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                inputMode="decimal"
                error={price.length > 0 && !valid}
                helperText={price.length > 0 && !valid ? 'Enter a positive amount' : ' '}
              />
              <TextField
                label="Note for buyers (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <Box>
                <Button
                  variant="contained"
                  disabled={!valid || publish.isPending}
                  onClick={() => publish.mutate()}
                >
                  {publish.isPending ? 'Publishing…' : 'Publish listing'}
                </Button>
              </Box>
              {publish.isError ? (
                <Alert severity="error">
                  {publish.error instanceof FseApiError
                    ? publish.error.message
                    : 'Could not publish this listing.'}
                </Alert>
              ) : null}
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

interface MatchesSectionProps {
  batchId: string;
  t: TFunction;
}

/**
 * Scored matches of this batch against open buyer demands (GET
 * /market/matches/:id, coop-scoped server-side). Each row shows the demand
 * product, offered price-per-kg, and a composite match score with its
 * component breakdown (qty / price / recency / proximity fits).
 */
function MatchesSection({ batchId, t }: MatchesSectionProps) {
  const matchesQuery = useMarketControllerGetMatches(batchId, {
    query: { enabled: batchId.length > 0, retry: false },
  });

  // MatchListDto wraps the scored demands in `data`. Widen to surface the
  // server's distanceBand (not on the generated MatchDto yet).
  const matches = (matchesQuery.data?.data ?? []) as MatchWithDistance[];

  return (
    <Stack spacing={1.5}>
      <Text variant="title-large" as="h3">
        {t('matches.title')}
      </Text>

      {/* Coop collection point — the GPS the distance bands below are measured
          FROM (founder decision: an aggregated batch's location is the coop's
          collection point). Capture once; the point is stored server-side and
          NEVER shown — only coarse bands appear on each match. */}
      <CollectionPointCard batchId={batchId} t={t} />

      {matchesQuery.isPending ? (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          </CardContent>
        </Card>
      ) : matchesQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => matchesQuery.refetch()}>
              {t('matches.retry')}
            </Button>
          }
        >
          {t('matches.loadError', {
            error:
              matchesQuery.error instanceof Error
                ? matchesQuery.error.message
                : String(matchesQuery.error),
          })}
        </Alert>
      ) : matches.length === 0 ? (
        <Card>
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {t('matches.empty')}
            </Text>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {matches.map((match) => (
            <MatchCard key={match.demandId} match={match} t={t} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

interface MatchCardProps {
  match: MatchWithDistance;
  t: TFunction;
}

function MatchCard({ match, t }: MatchCardProps) {
  const pricePerKg = microToZarp(match.priceZarpPerKgMicro);
  // Score is 0–1; present as a whole-percent fit for the StatCard headline.
  const scorePct = Math.round(match.score * 100);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Stack spacing={0.5}>
              <Text variant="title-medium" as="span">
                {match.product}
              </Text>
              <Text variant="body-medium" color="text.secondary">
                {match.qtyKgMin}–{match.qtyKgMax} kg
              </Text>
              <Money amount={pricePerKg} unit={t('matches.perKg')} size="sm" />
              {/* Coarse distance band from the coop collection point to this
                  buyer — only shown when both points are set. */}
              {match.distanceBand ? (
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    kind="assist"
                    color="neutral"
                    leadingIcon="pin"
                    label={distanceBandLabel(t, match.distanceBand)}
                  />
                </Box>
              ) : null}
            </Stack>
            <StatCard
              color="leaf"
              label={t('matches.matchScore')}
              value={`${scorePct}%`}
            />
          </Box>

          {/* Score component breakdown */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 1,
            }}
          >
            <ScoreChip label={t('matches.qtyFit')} value={match.qtyFit} />
            <ScoreChip label={t('matches.priceFit')} value={match.priceFit} />
            <ScoreChip label={t('matches.recency')} value={match.recency} />
            <ScoreChip label={t('matches.proximity')} value={match.proximity} />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

/** A small labelled 0–1 fit value, shown as a whole percent. */
function ScoreChip({ label, value }: { label: string; value: number }) {
  const pct = Number.isFinite(value) ? Math.round(value * 100) : 0;
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Text variant="title-small" as="span">
        {pct}%
      </Text>
      <Box sx={{ mt: 0.25 }}>
        <Text variant="label-small" color="text.secondary">
          {label}
        </Text>
      </Box>
    </Box>
  );
}

/**
 * Coop COLLECTION POINT capture (coop_admin only). Mirrors the buyer "Use my
 * location" affordance: grabs the browser GPS once and stores it server-side at
 * full precision — the point is NEVER shown, only the coarse distance bands on the
 * match cards above are derived from it. Gated to coop_admin (the GET 400s for a
 * caller who admins 0 or >1 coops, so we only fire it for a single-coop admin).
 */
function CollectionPointCard({ batchId, t }: { batchId: string; t: TFunction }) {
  const queryClient = useQueryClient();
  const role = readAccessTokenClaims()?.role;
  const isCoopAdmin = role === 'coop_admin';
  const [geoError, setGeoError] = useState<string | null>(null);

  const cpQuery = useQuery({
    queryKey: ['coop-collection-point'],
    queryFn: ({ signal }) => getMyCollectionPoint(signal),
    enabled: isCoopAdmin,
    retry: false,
  });
  const hasPoint = cpQuery.data?.hasCollectionPoint === true;

  const cpMutation = useMutation({
    mutationFn: setMyCollectionPoint,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coop-collection-point'] });
      // Re-score the matches so the distance bands recompute against the new point
      // (the orval matches query is keyed by batch id).
      void queryClient.invalidateQueries({
        queryKey: getMarketControllerGetMatchesQueryKey(batchId),
      });
    },
  });

  const capture = useCallback(() => {
    setGeoError(null);
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      setGeoError(t('matches.collectionPoint.unsupported', 'Location is not available in this browser.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => cpMutation.mutate({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () =>
        setGeoError(
          t('matches.collectionPoint.denied', 'Location access was denied. Allow it to set the collection point.'),
        ),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
    );
  }, [cpMutation, t]);

  // Only a coop_admin (the batch owner who can set a collection point) sees this.
  if (!isCoopAdmin) return null;
  // A non-single-coop admin gets a 400 on the read; just hide the affordance.
  if (cpQuery.isError) return null;

  const busy = cpMutation.isPending;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1.5}>
          <Text variant="title-small" as="span">
            {t('matches.collectionPoint.title', 'Collection point')}
          </Text>
          <Text variant="body-small" color="text.secondary">
            {t(
              'matches.collectionPoint.body',
              'Set where buyers collect from your cooperative. Distances on the matches above are measured from this point. The location is stored privately and never shown to buyers — only a coarse distance band.',
            )}
          </Text>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon name="pin" size={18} aria-hidden />}
              onClick={capture}
              disabled={busy}
            >
              {busy
                ? t('matches.collectionPoint.locating', 'Locating…')
                : hasPoint
                  ? t('matches.collectionPoint.update', 'Update collection point')
                  : t('matches.collectionPoint.use', 'Use my location')}
            </Button>
            {hasPoint ? (
              <Text variant="label-medium" color="text.secondary">
                {t('matches.collectionPoint.set', 'Collection point set — distances shown above')}
              </Text>
            ) : null}
            {geoError ? (
              <Text variant="label-medium" color="error.main">
                {geoError}
              </Text>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
